/**
 * propuestaCredito — genera una "Propuesta referencial de crédito" formal y
 * con marca, en el mismo estilo que la propuesta de inversión en Plazo Fijo:
 * carta institucional con header (logo + razón social + regla naranja),
 * cuerpo formal, "Resumen del escenario referencial", tabla de amortización,
 * cláusulas, firma del representante legal y footer con datos de contacto.
 *
 * Se imprime abriendo una ventana nueva y llamando print() (mismo patrón que
 * imprimirCdp.ts). No usa librería de PDF: el usuario elige "Guardar como PDF".
 */
import { LOGO_HORIZONTAL } from '@neocoop/cert-pdf'

// ── Marca ────────────────────────────────────────────────
const BRAND = '#FF5B04'
const BRAND_DEEP = '#c43c00'
const BRAND_SOFT = '#fff3ec'

// Institucional
const COOP_RAZON = 'Cooperativa Integral de Ahorro y Crédito, Neo R.L.'
const COOP_DIR = 'El Cubo Center, OF. 209a'
const COOP_TEL = '+502 5865-8811'
const COOP_MAIL = 'info@neocoop.gt'
const FIRMANTE = 'Ing. Hugo Duarte'
const FIRMANTE_CARGO = 'Presidente del Consejo y Representante Legal.'
const FIRMANTE_MAIL = 'hugo.duarte@neocoop.gt'

// Isotipo (marca de agua) — mismos paths que documentos.ts
const ISO_PATHS = [
  'M359.94,322.19c-13.23,1.78-25.17.27-37.67.15l-46.79-39.77c-24.65-20.95-103.18-70.9-134.87-52.87-12.74,7.26-17.32,20.71-13,34.38l18.58,58.75c-38.96-2.37-67.17-34.16-68.2-70.94-1.04-37.04,26.15-70.32,63.88-74.52,12.09-1.34,23.87-.27,35.97-.12l33.62,30.13c32.56,29.18,78.51,61.81,120.03,75.8,13.76,4.63,27.85.28,35.8-11.86,6.68-9.29,8.63-20.53,5.32-31.7l-18.62-62.8c37.72,2.55,66.43,33.34,67.99,69.81,1.56,36.72-24.11,70.43-62.04,75.55Z',
  'M322.51,353.67c-2.8,43.51-43.03,73.79-86.5,65.54-34.64-6.58-58.91-37.63-58.58-73.3l.74-80.43c6.33,31.17,16.02,60.42,35.56,85.15,12.01,15.03,28.93,23.14,47.89,20.76l60.89-17.72Z',
  'M322.68,235.34c-6.92-31.42-16.76-69.58-41.19-92.74-13.2-12.52-30.95-17.47-48.54-12.27l-55.49,16.41c2.69-34.09,28.27-62.13,62.31-66.65,40.51-5.38,79.72,22.48,82.7,64.27,2.17,30.48.34,59.97.22,90.98Z',
]
const isoSvg = (px: number, color: string, opacity = 1) =>
  `<svg viewBox="0 0 500 500" width="${px}" height="${px}" style="opacity:${opacity}">${ISO_PATHS.map(
    (d) => `<path fill="${color}" d="${d}"/>`,
  ).join('')}</svg>`

// ── Tipos ────────────────────────────────────────────────
export type PropuestaCuota = {
  numero: number
  fecha: string
  capital: number
  interes: number
  total: number
  cuota_cobrada?: number
  aporte_aportaciones?: number
  saldo_post: number
}
export type PropuestaCreditoParams = {
  clienteNombre: string
  productoNombre: string
  monto: number
  numCuotas: number
  periodicidad: string // 'mensual' | 'quincenal' | ...
  tasaPct: number // ej. 3 (por %)
  cuotaPeriodo: number
  totalInteres: number
  totalAPagar: number
  totalAporte?: number
  redondea?: boolean
  fechaPrimera: string
  fechaUltima: string
  cuotas: PropuestaCuota[]
}

// ── Formato ──────────────────────────────────────────────
const q = (n: number) =>
  'Q' + Number(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const fecha = (iso: string) =>
  new Date(String(iso).slice(0, 10) + 'T12:00:00').toLocaleDateString('es-GT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
const hoyLargo = () =>
  new Date().toLocaleDateString('es-GT', { day: 'numeric', month: 'long', year: 'numeric' })

const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s)
const periodoAdj = (p: string) =>
  p === 'mensual' ? 'mensuales' : p === 'quincenal' ? 'quincenales' : p === 'semanal' ? 'semanales' : `${p}`

// ── HTML ─────────────────────────────────────────────────
export function propuestaCreditoHtml(p: PropuestaCreditoParams): string {
  const cliente = (p.clienteNombre || '').trim() || 'Estimado(a) asociado(a)'
  const saludo = (p.clienteNombre || '').trim() ? `Estimado(a) ${cap(p.clienteNombre.trim())}:` : 'Estimado(a) asociado(a):'
  void cliente

  const filas = p.cuotas
    .map(
      (c) => `<tr>
      <td class="l">${c.numero}</td>
      <td class="l">${fecha(c.fecha)}</td>
      <td>${q(c.capital)}</td>
      <td>${q(c.interes)}</td>
      <td class="b">${q(c.cuota_cobrada ?? c.total)}</td>
      ${p.redondea ? `<td>${q(c.aporte_aportaciones ?? 0)}</td>` : ''}
      <td>${q(Math.max(c.saldo_post, 0))}</td>
    </tr>`,
    )
    .join('')

  const totCap = p.cuotas.reduce((s, c) => s + c.capital, 0)

  return `<!doctype html><html lang="es"><head><meta charset="utf-8">
<title>Propuesta referencial de crédito — ${p.clienteNombre || 'Neocoop'}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
  @page { size: 210mm 297mm; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { font-family: Inter, system-ui, sans-serif; color: #1c1c1e; font-size: 12px; line-height: 1.6; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .page { position: relative; width: 210mm; min-height: 297mm; padding: 20mm 18mm 26mm; overflow: hidden; }
  .wm { position: absolute; top: 46%; left: 50%; transform: translate(-50%,-50%); opacity: .05; z-index: 0; }
  .content { position: relative; z-index: 1; }
  .hd { display: flex; align-items: center; gap: 16px; border-bottom: 3px solid ${BRAND}; padding-bottom: 14px; }
  .hd img { height: 58px; }
  .hd .rz { font-size: 17px; font-weight: 800; color: #1c1c1e; letter-spacing: .2px; line-height: 1.15; text-transform: uppercase; }
  .meta { margin-top: 18px; font-size: 12px; }
  .meta b { color: #1c1c1e; }
  .asunto { margin-top: 14px; font-weight: 700; }
  p { margin: 12px 0; text-align: justify; }
  .h { font-weight: 700; margin: 18px 0 6px; }
  ul.res { margin: 6px 0 6px; padding-left: 18px; }
  ul.res li { margin: 3px 0; }
  ul.res b { }
  table { width: 100%; border-collapse: collapse; font-size: 11px; margin-top: 6px; }
  th { text-align: right; padding: 7px 8px; font-size: 9px; font-weight: 800; letter-spacing: .4px; text-transform: uppercase; color: ${BRAND_DEEP}; background: ${BRAND_SOFT}; border-bottom: 1.5px solid #f0c9a8; }
  th.l, td.l { text-align: left; }
  td { padding: 6px 8px; text-align: right; border-bottom: 1px solid #f2f1ef; font-variant-numeric: tabular-nums; }
  td.b { font-weight: 700; }
  tr.tot td { font-weight: 800; background: #fafafa; border-top: 1.5px solid #e7e7e7; }
  .firma { margin-top: 34px; }
  .firma .nm { font-weight: 700; }
  .foot { position: absolute; left: 0; right: 0; bottom: 0; padding: 10px 18mm; border-top: 3px solid ${BRAND}; display: flex; align-items: center; justify-content: center; gap: 26px; font-size: 10.5px; color: #444; }
  .foot .it { display: inline-flex; align-items: center; gap: 6px; }
  .dot { width: 15px; height: 15px; border-radius: 50%; background: ${BRAND}; display: inline-block; }
  .disc { font-size: 10.5px; color: #555; }
</style></head>
<body>
  <div class="page">
    <div class="wm">${isoSvg(430, BRAND, 1)}</div>
    <div class="content">
      <div class="hd">
        <img src="${LOGO_HORIZONTAL}" alt="neocoop">
        <div class="rz">Cooperativa Integral de Ahorro<br>y Crédito Neo R.L.</div>
      </div>

      <div class="meta">
        <b>neocoop.</b><br>
        ${COOP_RAZON}<br>
        Guatemala, ${hoyLargo()}
      </div>

      <div class="asunto">Asunto: Propuesta referencial de crédito — ${p.productoNombre}</div>

      <p style="margin-top:16px">${saludo}</p>

      <p>Reciba un cordial saludo de parte de <b>neocoop.</b>, nombre comercial de la <b>${COOP_RAZON}</b>, entidad legalmente constituida en Guatemala.</p>

      <p>Por medio de la presente, nos permitimos compartir un escenario referencial para un <b>crédito de ${p.productoNombre.toLowerCase()}</b>, estructurado bajo condiciones claras, formales y debidamente documentadas. Ponemos a su consideración el siguiente escenario para un crédito de <b>${q(p.monto)}</b> a <b>${p.numCuotas} cuota${p.numCuotas === 1 ? '' : 's'} ${periodoAdj(p.periodicidad)}</b>, con <b>tasa fija del ${p.tasaPct.toFixed(2)}% ${p.periodicidad}</b> calculada sobre el capital, conforme a la corrida emitida por nuestro sistema institucional.</p>

      <div class="h">Resumen del escenario referencial</div>
      <ul class="res">
        <li><b>Producto:</b> ${p.productoNombre}</li>
        <li><b>Monto del crédito:</b> ${q(p.monto)}</li>
        <li><b>Plazo:</b> ${p.numCuotas} cuota${p.numCuotas === 1 ? '' : 's'} ${periodoAdj(p.periodicidad)} (${fecha(p.fechaPrimera)} — ${fecha(p.fechaUltima)})</li>
        <li><b>Tasa:</b> ${p.tasaPct.toFixed(2)}% ${p.periodicidad}, fija sobre el capital</li>
        <li><b>Cuota ${p.periodicidad}:</b> ${q(p.cuotaPeriodo)}</li>
        <li><b>Intereses totales estimados:</b> ${q(p.totalInteres)}</li>
        <li><b>Total a pagar:</b> ${q(p.totalAPagar)}</li>
        ${p.redondea ? `<li><b>Aporte a aportaciones (redondeo):</b> ${q(p.totalAporte ?? 0)}</li>` : ''}
      </ul>

      <div class="h">Plan de pagos</div>
      <table>
        <thead><tr>
          <th class="l">N.º</th><th class="l">Vence</th><th>Capital</th><th>Interés</th><th>Cuota</th>${p.redondea ? '<th>Aporte</th>' : ''}<th>Saldo</th>
        </tr></thead>
        <tbody>
          ${filas}
          <tr class="tot"><td class="l">—</td><td class="l">Totales</td><td>${q(totCap)}</td><td>${q(p.totalInteres)}</td><td>${q(p.totalAPagar)}</td>${p.redondea ? `<td>${q(p.totalAporte ?? 0)}</td>` : ''}<td>Q0.00</td></tr>
        </tbody>
      </table>

      <p class="disc" style="margin-top:14px">La presente propuesta tiene carácter <b>referencial e informativo</b> y no constituye una oferta vinculante. La solicitud queda sujeta a evaluación y aprobación; las condiciones finales se formalizan en el pagaré y demás instrumentos correspondientes, conforme a la normativa interna aplicable al producto.</p>

      <p style="margin-top:18px">En caso de que este escenario sea de su interés, con gusto le acompañamos en el proceso de solicitud y formalización. Quedamos a su disposición para ampliar cualquier punto.</p>

      <div class="firma">
        <p style="margin:0">Atentamente,</p>
        <div style="height:52px"></div>
        <div class="nm">${FIRMANTE}</div>
        <div>${FIRMANTE_CARGO}</div>
        <div>neocoop. · ${COOP_RAZON}</div>
        <div>${COOP_TEL} · ${FIRMANTE_MAIL}</div>
      </div>
    </div>

    <div class="foot">
      <span class="it"><span class="dot"></span> ${COOP_DIR}</span>
      <span class="it"><span class="dot"></span> ${COOP_TEL}</span>
      <span class="it"><span class="dot"></span> ${COOP_MAIL}</span>
    </div>
  </div>
</body></html>`
}

// ── Impresión ────────────────────────────────────────────
export function imprimirPropuestaCredito(p: PropuestaCreditoParams): void {
  const html = propuestaCreditoHtml(p)
  const w = window.open('', '_blank', 'width=900,height=1200')
  if (!w) {
    alert('Permití las ventanas emergentes para poder imprimir/guardar la propuesta.')
    return
  }
  w.document.open()
  w.document.write(html)
  w.document.close()
  const doPrint = () => {
    try {
      w.focus()
      w.print()
    } catch {
      /* noop */
    }
  }
  // Esperar a que cargue el logo (imagen base64) antes de imprimir.
  if (w.document.readyState === 'complete') setTimeout(doPrint, 400)
  else w.onload = () => setTimeout(doPrint, 400)
}
